import { Controller } from "@hotwired/stimulus";
import { scrollHorizontallyBy } from "../utils/index_scroll_table_scroll";

// Navigation clavier sur les index : ↑/↓ promènent une surbrillance de ligne en ligne et
// Entrée ouvre la show — même ressenti que les résultats de la recherche globale, dont on
// reprend la pastille ↵ (elle remplace le menu « … » sur la ligne active, cf. CSS).
//
// La ligne active porte une classe, elle ne prend pas le focus : les lignes ne sont pas des
// cibles focalisables et c'est nous qui décidons ce que fait Entrée. Le focus, lui, fait
// l'aller-retour avec la recherche de l'index : ↓ depuis le champ entre dans la liste et rend
// le clavier au tableau (sinon ←/→ resteraient pris par le curseur de saisie), ↑ au-dessus de
// la première ligne y ramène, curseur en fin de requête.
//
// ←/→ font défiler le tableau scrollable. Le conteneur est déjà focusable (tabindex=0, cf.
// _scroll_table.html.erb) donc le navigateur sait le faire — mais seulement quand il a le
// focus, et par sauts de ~40 px secs. On reprend la main pour que les flèches marchent sans
// avoir cliqué dedans, avec un pas lisible et une animation (cf. index_scroll_table_scroll,
// partagé avec les boutons ‹ › de l'en-tête pour que les deux gestes avancent du même pas).
//
// L'ouverture passe par un clic sur le lien de la ligne, jamais par Turbo.visit : c'est le
// clic qui déclenche `turbo:click`, donc le squelette de nav instantané et le prefetch.
// Corollaire : une ligne dont le lien show est absent n'est pas navigable.
const ACTIVE_CLASS = 'is-keyboard-active';
const ROW_SELECTOR = '[data-index-nav-url]';
const SCROLLER_SELECTOR = '.index-scroll-table';
const TAB_SELECTOR = '[role="tab"], [data-bs-toggle="tab"]';
// La classe `show-sections-nav` sert aussi ailleurs (onglets du tableau de bord) : on vise le
// contrôleur de la show, seul à posséder les onglets qui doivent capter ←/→.
const SHOW_TABS_SELECTOR = '[data-controller~="show-sections"] [role="tablist"]';
const EDITABLE_SELECTOR = 'input, textarea, select, [contenteditable=""], [contenteditable="true"]';
const VERTICAL_KEYS = ['ArrowDown', 'ArrowUp'];
const HORIZONTAL_KEYS = ['ArrowRight', 'ArrowLeft'];
const HANDLED_KEYS = [...VERTICAL_KEYS, ...HORIZONTAL_KEYS, 'Enter', 'Escape'];
// Touche maintenue : le keydown se répète ~30 fois/s, et autant de scrolls « smooth » enchaînés
// traversent le tableau d'un coup. Un pas toutes les 150 ms donne un défilement continu tenable.
const SCROLL_THROTTLE_MS = 150;

// Une page peut porter plusieurs index (sections d'une show, tableaux de bord) : ils écoutent
// tous le document, un seul répond — cf. `isActiveIndex`.
const mounted = new Set();

export default class extends Controller {
  connect() {
    this.boundKeyDown = this.handleKeyDown.bind(this);
    this.boundPointerMove = this.handlePointerMove.bind(this);
    this.boundClearSelection = this.clearSelection.bind(this);

    mounted.add(this);
    // En capture : sur une show, Bootstrap traite ↑/↓ sur l'onglet qui a le focus et change
    // d'onglet. Il faut donc pouvoir couper avant lui — ←/→ lui restent acquis.
    document.addEventListener('keydown', this.boundKeyDown, true);
    // turbo:before-cache prend le snapshot de la page qu'on QUITTE : sans ce nettoyage,
    // un retour arrière réaffiche une ligne surlignée que plus rien ne pilote.
    document.addEventListener('turbo:before-cache', this.boundClearSelection);
    this.element.addEventListener('mousemove', this.boundPointerMove);
  }

  disconnect() {
    mounted.delete(this);
    document.removeEventListener('keydown', this.boundKeyDown, true);
    document.removeEventListener('turbo:before-cache', this.boundClearSelection);
    this.element.removeEventListener('mousemove', this.boundPointerMove);
    this.clearSelection();
  }

  handleKeyDown(event) {
    if (!HANDLED_KEYS.includes(event.key)) return;
    if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return;
    if (this.isSuspended) return;
    if (!this.isActiveIndex) return;
    if (!this.acceptsKeysFrom(event.target)) return;

    if (event.key === 'Escape') {
      // Pas de preventDefault : Escape sert aussi à fermer la recherche, au-dessus de nous.
      this.clearSelection();
      return;
    }

    if (event.key === 'Enter') {
      const active = this.activeRow;
      if (!active) return;

      this.take(event);
      this.openRow(active);
      return;
    }

    if (HORIZONTAL_KEYS.includes(event.key)) {
      // Jamais dans un champ : ←/→ y déplacent le curseur de saisie, ça prime. Ni sur les
      // onglets d'une show : ←/→ y passent d'un onglet à l'autre, c'est leur rôle.
      if (this.isEditable(event.target) || this.isTab(event.target)) return;
      // Et sur une show, les onglets gardent ←/→ même sans focus (cf. show_sections_controller).
      // Le tableau d'une section ne les reprend que si on a cliqué dedans : il est focusable
      // exprès (tabindex=0), c'est le geste qui dit « je travaille dans ce tableau ».
      if (this.showTabsVisible && !this.hasScrollerFocus(event.target)) return;
      if (this.scrollHorizontally(event.key === 'ArrowRight' ? 1 : -1)) this.take(event);
      return;
    }

    const rows = this.navigableRows();
    if (!rows.length) {
      // Un onglet a le focus et nos lignes ne sont pas encore arrivées : on garde quand même
      // ↑/↓, sinon Bootstrap change d'onglet sous les doigts de l'utilisateur.
      if (this.isTab(event.target)) this.take(event);
      return;
    }

    this.take(event);
    this.moveSelection(event.key === 'ArrowDown' ? 1 : -1, rows);
  }

  // On écoute en capture : couper la propagation empêche les gestionnaires de la cible
  // (onglets Bootstrap, recherche de l'index) de rejouer la même touche derrière nous.
  take(event) {
    event.preventDefault();
    event.stopPropagation();
  }

  // La souris reprend la main dès qu'elle bouge pour de vrai : deux lignes allumées
  // (hover CSS + clavier) sont plus troublantes qu'utiles. Le scroll, lui, n'émet pas de
  // mousemove — promener la sélection sous un curseur immobile reste donc possible.
  handlePointerMove(event) {
    const active = this.activeRow;
    if (!active) return;

    const row = event.target instanceof Element ? event.target.closest(ROW_SELECTOR) : null;
    if (row === active) return;

    this.clearSelection();
  }

  // Une couche par-dessus (modale, dropdown d'actions, recherche globale) gère ses propres
  // flèches : l'index se tait tant qu'elle est ouverte.
  get isSuspended() {
    const { classList } = document.body;
    if (
      classList.contains('modal-open') ||
      classList.contains('global-search-palette-open') ||
      classList.contains('global-search-inline-active')
    ) {
      return true;
    }

    return Boolean(document.querySelector('.dropdown-menu.show'));
  }

  // Un seul index répond aux touches : celui qui contient le focus, sinon le premier visible
  // qui a des lignes (une show affiche l'index de sa section active, pas ceux des autres).
  get isActiveIndex() {
    const visible = [...mounted].filter((controller) => controller.isVisible);
    if (visible.length <= 1) return visible[0] === this;

    const focused = visible.find((controller) => controller.element.contains(document.activeElement));
    if (focused) return focused === this;

    const withRows = visible.filter((controller) => controller.navigableRows().length > 0);
    const candidates = withRows.length ? withRows : visible;
    candidates.sort((a, b) =>
      a.element.compareDocumentPosition(b.element) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1
    );

    return candidates[0] === this;
  }

  get isVisible() {
    return this.element.isConnected && this.element.getClientRects().length > 0;
  }

  acceptsKeysFrom(target) {
    if (!(target instanceof Element)) return true;
    if (target.closest('.global-search')) return false;

    const editable = this.editableAround(target);
    if (!editable) return true;

    // Seule la barre de recherche de CET index laisse passer ↑/↓ : on affine sa requête puis
    // on descend dans la liste sans lâcher le clavier (et le champ perd alors le focus, cf.
    // `select`). Un champ de saisie rapide dans une ligne garde son comportement natif.
    return this.element.contains(editable) && editable.matches('[data-search-target="input"]');
  }

  isEditable(target) {
    return target instanceof Element && Boolean(this.editableAround(target));
  }

  editableAround(target) {
    return target.closest(EDITABLE_SELECTOR);
  }

  isTab(target) {
    return target instanceof Element && Boolean(target.closest(TAB_SELECTOR));
  }

  get showTabsVisible() {
    const tabs = document.querySelector(SHOW_TABS_SELECTOR);
    return Boolean(tabs && tabs.getClientRects().length > 0);
  }

  hasScrollerFocus(target) {
    return target instanceof Element && Boolean(target.closest(SCROLLER_SELECTOR));
  }

  get activeRow() {
    return this.element.querySelector(`${ROW_SELECTOR}.${ACTIVE_CLASS}`);
  }

  // Recalculé à chaque frappe : les previews de ligne arrivent en turbo-frame paresseuses,
  // et la recherche remplace la liste entière à chaque lettre.
  navigableRows() {
    return Array.from(this.element.querySelectorAll(ROW_SELECTOR)).filter(
      (row) => row.getClientRects().length > 0 && this.rowLink(row)
    );
  }

  rowLink(row) {
    const raw = row.dataset.indexNavUrl;
    if (!raw) return null;

    let target;
    try {
      target = new URL(raw, window.location.origin);
    } catch {
      return null;
    }

    return (
      Array.from(row.querySelectorAll('a[href]')).find((link) => {
        try {
          return new URL(link.href).pathname === target.pathname;
        } catch {
          return false;
        }
      }) || null
    );
  }

  moveSelection(delta, rows) {
    const current = rows.indexOf(this.activeRow);

    if (delta < 0) {
      // Une flèche haut de plus que la première ligne : on ressort du tableau par le haut,
      // c'est-à-dire dans la recherche — d'où on redescend avec ↓. Rien à faire si le curseur
      // y est déjà : remonter depuis le champ n'a pas à sauter en bas de liste.
      if (current === 0) {
        if (this.focusSearchInput()) return;
      } else if (current === -1 && document.activeElement === this.searchInput) {
        return;
      }
    }

    // Sinon on s'arrête aux extrémités : sur 25 lignes, un wrap renverrait en haut de page
    // sans prévenir — la pagination est là pour changer de page.
    const next =
      current === -1
        ? (delta > 0 ? 0 : rows.length - 1)
        : Math.min(Math.max(current + delta, 0), rows.length - 1);

    this.select(rows[next]);
  }

  get searchInput() {
    return this.element.querySelector('[data-search-target="input"]');
  }

  focusSearchInput() {
    const input = this.searchInput;
    if (!input || input.getClientRects().length === 0) return false;

    this.clearSelection();
    // Focus sans preventScroll : si la liste est descendue, la recherche doit revenir à l'écran.
    input.focus();
    // Curseur en fin de requête : on reprend la frappe là où on l'avait laissée.
    const length = input.value.length;
    input.setSelectionRange?.(length, length);

    return true;
  }

  select(row) {
    this.clearSelection();
    if (!row) return;

    // Entrer dans la liste, c'est sortir du champ : ←/→ doivent faire défiler le tableau,
    // pas déplacer un curseur de saisie resté derrière.
    const input = this.searchInput;
    if (input && document.activeElement === input) input.blur();

    row.classList.add(ACTIVE_CLASS);
    row.scrollIntoView({ block: 'nearest' });
  }

  clearSelection() {
    this.element
      .querySelectorAll(`${ROW_SELECTOR}.${ACTIVE_CLASS}`)
      .forEach((row) => row.classList.remove(ACTIVE_CLASS));
  }

  openRow(row) {
    const link = this.rowLink(row);
    if (!link) return;

    this.clearSelection();
    link.click();
  }

  // Le premier `.index-scroll-table` du DOM n'est pas forcément le bon : les squelettes de
  // chargement en laissent de taille nulle derrière eux. On prend celui qui est affiché ET
  // qui déborde vraiment.
  get scroller() {
    return (
      Array.from(this.element.querySelectorAll(SCROLLER_SELECTOR)).find(
        (element) =>
          element.getClientRects().length > 0 && element.scrollWidth > element.clientWidth + 1
      ) || null
    );
  }

  scrollHorizontally(direction) {
    const scroller = this.scroller;
    if (!scroller) return false;

    const now = Date.now();
    if (now - (this.lastScrollAt || 0) < SCROLL_THROTTLE_MS) return true;
    this.lastScrollAt = now;

    scrollHorizontallyBy(scroller, direction);

    return true;
  }
}
